'use client';
import { useCallback, useEffect, useMemo, useRef } from 'react';

interface PacedCommand {
  /** a screen is replaced by a newer one while waiting; other commands are all sent */
  isDisplay: boolean;
  data: string;
}

/**
 * How many commands a glove's queue in Insight Mobile takes: `burst` at once,
 * then one more every `refillMs` as it works them off.
 */
export interface CommandBudget {
  burst: number;
  refillMs: number;
}

export interface CommandPacer {
  /** sends a screen; a screen still waiting to go out is replaced by this one */
  sendDisplay: (data: string) => void;
  /** sends any other command, e.g. feedback!; all of them go out, in order */
  sendCommand: (data: string) => void;
  /** drops everything still waiting, e.g. when the connection is lost */
  clear: () => void;
}

/**
 * Keeps the commands to a glove within what Insight Mobile accepts. It queues
 * them per device and refuses more once the queue is full ("Command queue is
 * already at maximum capacity"); screens and feedback share that queue
 * (device-measured 2026-10-07: MAI takes 6 at once and works off one every
 * ~90 ms, MARK Display 5 at once and one screen every ~700 ms).
 *
 * Commands go out right away while the `budget` lasts, so a screen and its
 * feedback arrive together. Beyond it they wait for the budget to refill, and
 * of the screens waiting only the latest goes out: a stale screen would only
 * take the glove's time.
 */
export default function useCommandPacer(send: (data: string) => boolean, budget: CommandBudget): CommandPacer {
  const queueRef = useRef<PacedCommand[]>([]);
  const timerRef = useRef<number | undefined>(undefined);
  const sendRef = useRef(send);
  const budgetRef = useRef(budget);
  /** commands that may go out now, refilled over time up to the burst */
  const tokensRef = useRef<number>(budget.burst);
  const refilledAtRef = useRef<number>(Date.now());

  useEffect(() => {
    sendRef.current = send;
  }, [send]);

  useEffect(() => {
    budgetRef.current = budget;
    tokensRef.current = Math.min(tokensRef.current, budget.burst);
  }, [budget]);

  const refill = useCallback((): void => {
    const { burst, refillMs } = budgetRef.current;
    const now = Date.now();
    if (refillMs <= 0) {
      tokensRef.current = burst;
      refilledAtRef.current = now;
      return;
    }
    const earned = Math.floor((now - refilledAtRef.current) / refillMs);
    if (earned > 0) {
      tokensRef.current = Math.min(burst, tokensRef.current + earned);
      refilledAtRef.current = tokensRef.current === burst ? now : refilledAtRef.current + earned * refillMs;
    }
  }, []);

  const drain = useCallback((): void => {
    timerRef.current = undefined;
    refill();

    while (queueRef.current.length > 0 && tokensRef.current >= 1) {
      const next = queueRef.current.shift();
      if (next !== undefined) {
        tokensRef.current -= 1;
        sendRef.current(next.data);
      }
    }

    if (queueRef.current.length > 0) {
      const { refillMs } = budgetRef.current;
      const waitMs = Math.max(0, refilledAtRef.current + refillMs - Date.now());
      timerRef.current = window.setTimeout(drain, waitMs);
    }
  }, [refill]);

  const enqueue = useCallback(
    (command: PacedCommand): void => {
      const waitingDisplay = command.isDisplay ? queueRef.current.findIndex((queued) => queued.isDisplay) : -1;
      if (waitingDisplay >= 0) {
        queueRef.current[waitingDisplay] = command;
      } else {
        queueRef.current.push(command);
      }

      // while waiting for the budget, the scheduled drain sends it
      if (timerRef.current === undefined) {
        drain();
      }
    },
    [drain],
  );

  const clear = useCallback((): void => {
    window.clearTimeout(timerRef.current);
    timerRef.current = undefined;
    queueRef.current = [];
  }, []);

  useEffect(() => clear, [clear]);

  return useMemo(
    () => ({
      sendDisplay: (data: string) => enqueue({ isDisplay: true, data }),
      sendCommand: (data: string) => enqueue({ isDisplay: false, data }),
      clear,
    }),
    [enqueue, clear],
  );
}
